import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import { get, post, patch, del, API } from '../lib/api';
import axios from 'axios';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import KpiCard from '../components/widgets/KpiCard';
import EmptyState from '../components/widgets/EmptyState';
import CourseDetailDialog from '../components/CourseDetailDialog';
import { fmtBRL, fmtPct, fmtDate, todayISO, competenceLabel } from '../lib/format';
import {
  GraduationCap, Plus, Trash2, TrendingUp, Users, DollarSign, Percent,
  Trophy, Pencil, FileText, Upload, Download, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const STATUS_PILL = {
  PLANEJADO: 'pill-warning',
  EM_ANDAMENTO: 'pill-gold',
  REALIZADO: 'pill-success',
  CANCELADO: 'pill-muted',
};

const blank = () => ({
  company_id: '',
  name: '',
  code: '',
  instructor: '',
  location: '',
  start_date: todayISO(),
  end_date: '',
  students_enrolled: 0,
  max_students: 0,
  target_audience: '',
  status: 'PLANEJADO',
  notes: '',
});

export default function Cursos() {
  const { companies, companyFilter, competence } = useApp();
  const [ranking, setRanking] = useState([]);
  const [dashKpis, setDashKpis] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blank());
  const [detailCourse, setDetailCourse] = useState(null);

  const load = async () => {
    const cid = companyFilter === 'all' ? null : companyFilter;
    const params = { competence };
    if (cid) params.company_id = cid;
    const [r, k] = await Promise.all([
      get('/courses/ranking/profitability', params),
      get('/courses/dashboard-kpis', params),
    ]);
    setRanking(r);
    setDashKpis(k);
  };
  useEffect(() => { load(); }, [companyFilter, competence]);

  const openNew = () => { setEditId(null); setForm(blank()); setOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      company_id: c.company_id, name: c.name, code: c.code || '',
      instructor: c.instructor || '', location: c.location || '',
      start_date: c.start_date || todayISO(), end_date: c.end_date || '',
      students_enrolled: c.students_enrolled || 0, max_students: c.max_students || 0,
      target_audience: c.target_audience || '', status: c.status || 'PLANEJADO',
      notes: c.notes || '',
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.company_id || !form.name.trim()) return toast.error('Empresa e nome obrigatórios');
    const payload = {
      ...form,
      students_enrolled: parseInt(form.students_enrolled, 10) || 0,
      max_students: parseInt(form.max_students, 10) || 0,
      code: form.code || null,
      instructor: form.instructor || null,
      location: form.location || null,
      end_date: form.end_date || null,
      target_audience: form.target_audience || null,
      notes: form.notes || null,
    };
    try {
      if (editId) {
        await patch(`/courses/${editId}`, payload);
        toast.success('Curso atualizado');
      } else {
        await post('/courses', payload);
        toast.success('Curso criado');
      }
      setOpen(false); load();
    } catch (e) { toast.error('Erro'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir curso? Os lançamentos vinculados serão desvinculados (não excluídos).')) return;
    await del(`/courses/${id}`);
    toast.success('Excluído');
    load();
  };

  // -------- Import planilha de cursos --------
  const fileRef = useRef(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [importCompetence, setImportCompetence] = useState(todayISO().slice(0, 7));
  const [importReplace, setImportReplace] = useState(false);

  const downloadTemplate = () => {
    window.open(`${API}/courses/template-financial`, '_blank');
    toast.success('Template baixado');
  };

  const onImportClick = () => {
    setImportDialog(true);
  };

  const triggerFilePicker = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^\d{4}-\d{2}$/.test(importCompetence)) {
      return toast.error('Informe a competência YYYY-MM');
    }
    setImportBusy(true);
    setImportDialog(false);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('competence', importCompetence);
      fd.append('replace', String(importReplace));
      const { data } = await axios.post(`${API}/courses/import-financial`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      const msgParts = [
        `${data.courses_count} curso(s)`,
        `${data.clients_created || 0} NF(s) A Receber`,
        `${(data.costs_created || 0) + (data.extras_created || 0)} custo(s)`,
      ];
      toast.success(msgParts.join(' · ') + ' importados', { duration: 5000 });
      if (data.errors?.length) {
        console.warn('Erros:', data.errors);
        toast.error(`${data.errors.length} aba(s) com erro — veja console`, { duration: 6000 });
      }
      if (data.warnings?.length) {
        console.warn('Avisos:', data.warnings);
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao importar');
    } finally {
      setImportBusy(false);
    }
  };

  // KPIs consolidados (via /courses/dashboard-kpis)
  const totalReceita = dashKpis?.total_receita ?? 0;
  const totalCustos = dashKpis?.total_custos ?? 0;
  const totalDespesas = dashKpis?.total_despesas ?? 0;
  const totalLucro = dashKpis?.total_lucro ?? 0;
  const totalInscritos = dashKpis?.total_inscritos ?? 0;
  const margemGeral = dashKpis?.margem_geral ?? 0;
  const ticketMedio = dashKpis?.ticket_medio ?? 0;

  return (
    <div className="fade-in">
      <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onFileChange} style={{ display: 'none' }} />
      <TopBar
        title="Cursos & Eventos"
        subtitle={`Lucratividade da operação de cursos em ${competenceLabel(competence)} · Receita − (Custos Diretos + Despesas Gerais da Empresa) = Lucro`}
        action={
          <div className="flex gap-2">
            <Button
              onClick={downloadTemplate}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-900 gap-1.5"
              size="sm"
              data-testid="btn-download-course-template"
            >
              <Download size={14} /> Template
            </Button>
            <Button
              onClick={onImportClick}
              disabled={importBusy}
              variant="outline"
              className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 gap-1.5"
              size="sm"
              data-testid="btn-import-courses"
            >
              <Upload size={14} /> {importBusy ? 'Importando...' : 'Importar Planilha'}
            </Button>
            <Button onClick={openNew} className="btn-gold rounded-md gap-1.5 px-4" size="sm" data-testid="btn-new-course">
              <Plus size={15} /> Novo Curso
            </Button>
          </div>
        }
      />
      <div className="px-8 py-7 space-y-5">
        {/* KPIs macro da operação de cursos — 2 linhas para respirar visualmente */}
        {/* Linha 1: Financeiros (4 cards) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label="Receita Total" value={totalReceita} accent="#D4AF37" icon={DollarSign} testid="course-kpi-receita" highlight />
          <KpiCard label="Custo Total" value={totalCustos} accent="#EF4444" icon={TrendingUp} testid="course-kpi-custos" />
          <KpiCard label="Despesa Total" value={totalDespesas} accent="#F59E0B" icon={FileText} testid="course-kpi-despesas" />
          <KpiCard label="Lucro Total" value={totalLucro} accent={totalLucro >= 0 ? '#10B981' : '#EF4444'} icon={TrendingUp} testid="course-kpi-lucro" />
        </div>
        {/* Linha 2: Métricas (3 cards) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard label="Margem Geral" value={margemGeral} format="percent" accent="#3B82F6" icon={Percent} testid="course-kpi-margem" />
          <KpiCard label="Total de Inscritos" value={totalInscritos} format="number" accent="#8B5CF6" icon={Users} testid="course-kpi-inscritos" />
          <KpiCard label="Ticket Médio" value={ticketMedio} accent="#F59E0B" icon={Trophy} testid="course-kpi-ticket" />
        </div>

        <SectionCard
          title="Ranking de Cursos por Lucratividade"
          subtitle="Cada lançamento (receita/custo) pode ser vinculado a um curso na tela de Lançamentos ou via importação de planilha"
          padded={false}
          testid="courses-table"
        >
          {ranking.length === 0 ? (
            <div className="p-12">
              <EmptyState
                icon={GraduationCap}
                title={`Nenhum curso com NF emitida em ${competenceLabel(competence)}`}
                description="Cursos só aparecem aqui quando há pelo menos 1 NF emitida na competência selecionada. Troque a competência no topo, importe uma planilha de curso ou cadastre um curso manualmente."
                action={<Button onClick={openNew} className="btn-gold">Criar Curso</Button>}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full ceap-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Curso / Evento</th>
                    <th>Empresa</th>
                    <th className="text-center">Inscritos</th>
                    <th className="text-right">Receita</th>
                    <th className="text-right">Custos Diretos</th>
                    <th className="text-right">Lucro</th>
                    <th className="text-right">Margem</th>
                    <th className="text-right">Ticket Médio</th>
                    <th className="text-right">Custo/Aluno</th>
                    <th className="text-center">Status</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {ranking.map((c, i) => {
                    const co = companies.find((x) => x.id === c.company_id);
                    return (
                      <tr key={c.id} data-testid={`course-row-${c.id}`} onClick={() => setDetailCourse(c)} className="cursor-pointer hover:bg-zinc-900/40">
                        <td className="font-mono text-zinc-600 text-xs">#{i + 1}</td>
                        <td>
                          <div className="text-zinc-100 font-medium flex items-center gap-1.5">
                            {c.name} <ExternalLink size={11} className="text-zinc-500" />
                          </div>
                          {c.instructor && <div className="text-[11px] text-zinc-500">por {c.instructor}</div>}
                        </td>
                        <td>
                          <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-4 rounded-sm" style={{ background: co?.color || '#888' }} />
                            <span className="text-zinc-300 text-xs">{co?.name || '-'}</span>
                          </div>
                        </td>
                        <td className="text-center font-mono text-zinc-300">
                          {c.students_enrolled || 0}{c.max_students ? `/${c.max_students}` : ''}
                        </td>
                        <td className="text-right font-mono gold-text">{fmtBRL(c.receita)}</td>
                        <td className="text-right font-mono red-text">{fmtBRL(c.custos_diretos ?? c.saidas)}</td>
                        <td className={`text-right font-mono font-semibold ${c.lucro >= 0 ? 'emerald-text' : 'red-text'}`}>
                          {fmtBRL(c.lucro)}
                        </td>
                        <td className={`text-right font-mono ${c.margem >= 0 ? 'emerald-text' : 'red-text'}`}>
                          {fmtPct(c.margem)}
                        </td>
                        <td className="text-right font-mono text-zinc-300">{fmtBRL(c.ticket_medio)}</td>
                        <td className="text-right font-mono text-zinc-300">{fmtBRL(c.custo_por_aluno)}</td>
                        <td className="text-center">
                          <span className={`pill ${STATUS_PILL[c.status]} text-[10px]`}>{c.status.replace('_', ' ')}</span>
                        </td>
                        <td className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100" data-testid={`btn-edit-course-${c.id}`}>
                              <Pencil size={13} />
                            </button>
                            <button onClick={() => remove(c.id)} className="p-1.5 rounded hover:bg-red-900/30 text-zinc-400 hover:text-red-400" data-testid={`btn-delete-course-${c.id}`}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        <div className="flex items-start gap-3 px-4 py-3 bg-amber-950/20 border border-amber-700/40 rounded-md">
          <FileText size={16} className="text-amber-400 mt-0.5 shrink-0" />
          <div className="text-[12px] text-amber-100/80">
            <strong className="text-amber-300">Dica:</strong> Na página "Importar Fluxo de Caixa", clique em <strong className="gold-text">"Baixar Template de Cursos"</strong> (variação para empresas de cursos como Legislativo, & Lopes e Licitações). A planilha permite que seu financeiro vincule cada receita e custo a um curso específico — após upload, o lucro de cada curso aparece aqui automaticamente.
          </div>
        </div>
      </div>

      {/* DIALOG escolher competência antes de importar */}
      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="dialog-import-courses">
          <DialogHeader>
            <DialogTitle className="font-display">Importar Planilha de Cursos</DialogTitle>
            <div className="text-xs text-zinc-500 mt-1">
              Cada aba da planilha = 1 curso/evento. O sistema lê custos, NFs, inscritos e calcula lucratividade automaticamente.
            </div>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Competência *</Label>
              <Input
                value={importCompetence}
                onChange={(e) => setImportCompetence(e.target.value)}
                placeholder="2026-05"
                className="bg-zinc-900 border-zinc-800 mt-1 font-mono"
                data-testid="import-competence-input"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                {importCompetence && /^\d{4}-\d{2}$/.test(importCompetence) ? competenceLabel(importCompetence) : 'Use formato YYYY-MM'}
              </p>
            </div>
            <div className="text-xs text-emerald-200/80 bg-emerald-950/30 border border-emerald-700/40 rounded p-3">
              <strong className="emerald-text">Regra de negócio:</strong> As NFs do template entram <strong>SEMPRE como A RECEBER</strong> (Cobrança/Provisionado). Cursos & Eventos é controle de FATURAMENTO — quando o dinheiro for confirmado em conta, marque cada NF como <strong>"Recebida"</strong> no dialog do curso, e aí ela vai pro Fluxo Realizado e Dashboard.
            </div>

            <div className="text-xs text-zinc-400 bg-zinc-900/50 border border-zinc-800 rounded p-3">
              <strong className="text-zinc-200">Estrutura esperada da planilha:</strong>
              <ul className="mt-2 ml-4 list-disc space-y-1">
                <li>1 aba por curso/evento</li>
                <li>B4: nome do evento | B5: data | B6: local | B7: empresa</li>
                <li>Coluna B/C linhas 10-30: custos do curso</li>
                <li>Coluna H/I/J/K linhas 5+: Notas Fiscais (município, inscritos, valor, vendedora)</li>
                <li>Coluna E/F linhas 18+: extras do curso</li>
              </ul>
              <div className="mt-2 text-[11px] text-zinc-500">Não tem a planilha? Clique em <strong className="gold-text">"Template"</strong> para baixar o modelo.</div>
            </div>

            <label className="flex items-start gap-2 text-[12px] text-zinc-300 cursor-pointer p-2 rounded border border-zinc-800 bg-zinc-900/30 hover:bg-zinc-900/60">
              <input
                type="checkbox"
                checked={importReplace}
                onChange={(e) => setImportReplace(e.target.checked)}
                className="mt-0.5 rounded"
                data-testid="import-replace-checkbox"
              />
              <div>
                <div className="font-medium text-zinc-100">Substituir cursos existentes (replace)</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  Se o curso já existir, apaga clientes/custos antigos antes de reimportar. Use com cautela.
                </div>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImportDialog(false)} className="text-zinc-400 hover:bg-zinc-900">Cancelar</Button>
            <Button onClick={triggerFilePicker} className="btn-gold gap-1.5" data-testid="btn-pick-file">
              <Upload size={14} /> Selecionar Arquivo .xlsx
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG novo/editar curso */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="dialog-course">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? 'Editar Curso' : 'Novo Curso / Evento'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Empresa *</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1" data-testid="course-company"><SelectValue placeholder="Empresa" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  <SelectItem value="PLANEJADO">Planejado</SelectItem>
                  <SelectItem value="EM_ANDAMENTO">Em Andamento</SelectItem>
                  <SelectItem value="REALIZADO">Realizado</SelectItem>
                  <SelectItem value="CANCELADO">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome do Curso/Evento *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Ex: SC Legislativo - Comunicação MAR/2026" data-testid="course-name" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Código</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" placeholder="Opcional" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Instrutor / Palestrante</Label>
              <Input value={form.instructor} onChange={(e) => setForm({ ...form, instructor: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Local</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Ex: Hotel Castelmar - Florianópolis/SC" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Público-Alvo</Label>
              <Input value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" placeholder="Ex: Vereadores, Pregoeiros..." />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data Início</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data Fim</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Inscritos</Label>
              <Input type="number" value={form.students_enrolled} onChange={(e) => setForm({ ...form, students_enrolled: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" data-testid="course-enrolled" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Vagas Máximas</Label>
              <Input type="number" value={form.max_students} onChange={(e) => setForm({ ...form, max_students: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" />
            </div>
            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Observações</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-zinc-400 hover:bg-zinc-900">Cancelar</Button>
            <Button onClick={submit} className="btn-gold" data-testid="btn-save-course">{editId ? 'Atualizar' : 'Criar Curso'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CourseDetailDialog
        course={detailCourse}
        open={!!detailCourse}
        onClose={() => setDetailCourse(null)}
        onChanged={load}
      />
    </div>
  );
}
